import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronRight, Camera, Monitor, CheckSquare, Square } from 'lucide-react'
import { createUser } from '@/api/users'
import { getDevices, enrollUsersToDevice, type Device } from '@/api/devices'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

const USER_TYPES = [
  { value: 'normal',      label: 'Miembro regular' },
  { value: 'visitor',     label: 'Visitante' },
  { value: 'blackList',   label: 'Lista restringida' },
]

// Business-relationship category, shown to the front desk instead of the
// device-facing "Tipo" above (which stays tucked in Opciones de dispositivo —
// almost every person is "normal" there regardless of Cliente/Personal/Socio).
const MEMBER_CATEGORIES: { value: 'client' | 'staff' | 'partner'; label: string }[] = [
  { value: 'client',  label: 'Miembro' },
  { value: 'staff',   label: 'Personal' },
  { value: 'partner', label: 'Socio' },
]

const VERIFY_MODES = [
  { value: '',                   label: '(Predeterminado del dispositivo)' },
  { value: 'card',               label: 'Solo tarjeta' },
  { value: 'cardOrPw',           label: 'Tarjeta o PIN' },
  { value: 'cardAndPw',          label: 'Tarjeta y PIN' },
  { value: 'fp',                 label: 'Solo huella digital' },
  { value: 'fpOrCard',           label: 'Huella o tarjeta' },
  { value: 'fpAndCard',          label: 'Huella y tarjeta' },
  { value: 'faceOrFpOrCardOrPw', label: 'Cara, huella, tarjeta o PIN' },
]

const BLOOD_TYPES = ['A+', 'A−', 'B+', 'B−', 'AB+', 'AB−', 'O+', 'O−']
const GYM_GOALS = [
  'Bajar de peso', 'Ganar músculo', 'Mejorar condición física',
  'Rehabilitación / Lesión', 'Rendimiento deportivo', 'Mantenimiento', 'Otro',
]

function calcAge(birth: string): number | null {
  if (!birth) return null
  const b = new Date(birth + 'T12:00:00')
  if (isNaN(b.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - b.getFullYear()
  const m = today.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--
  return age
}

// Builds a "YYYY-MM-DDTHH:mm" string from the LOCAL calendar date/time — never
// use toISOString() here, it shifts by the browser's UTC offset and silently
// moves the displayed date/time by several hours (or a whole day near midnight).
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function getDefaultBeginTime() {
  const d = new Date(); d.setHours(0, 0, 0, 0)
  return toLocalInputValue(d)
}
function getDefaultEndTime() {
  const d = new Date(); d.setFullYear(d.getFullYear() + 1); d.setHours(23, 59, 0, 0)
  return toLocalInputValue(d)
}

const field = 'bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent w-full'
const lbl   = 'text-text-muted text-xs mb-1 block'

export function MemberForm() {
  const navigate = useNavigate()

  const [loading, setLoading]     = useState(false)
  const [errors, setErrors]       = useState<Record<string, string>>({})
  const [showGymData, setShowGymData]   = useState(true)
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Core fields
  const [name, setName]                     = useState('')
  const [employeeNo, setEmployeeNo]         = useState('')
  const [userType, setUserType]             = useState('normal')
  const [longTerm, setLongTerm]             = useState(false)
  const [memberCategory, setMemberCategory] = useState<'client' | 'staff' | 'partner'>('client')
  const [showDeviceReminder, setShowDeviceReminder] = useState(false)
  const [beginTime, setBeginTime]           = useState(getDefaultBeginTime)
  const [endTime, setEndTime]               = useState(getDefaultEndTime)

  // Personal fields
  const [phone, setPhone]                   = useState('')
  const [email, setEmail]                   = useState('')
  const [documentType, setDocumentType]     = useState('')
  const [birthDate, setBirthDate]           = useState('')
  const [bloodType, setBloodType]           = useState('')
  const [gender, setGender]                 = useState('')
  const [address, setAddress]               = useState('')
  const [occupation, setOccupation]         = useState('')
  const [emergencyName, setEmergencyName]   = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [goal, setGoal]                     = useState('')
  const [medicalNotes, setMedicalNotes]     = useState('')

  // Advanced (device settings)
  const [department, setDepartment]         = useState('')
  const [company, setCompany]               = useState('')
  const [userVerifyMode, setUserVerifyMode] = useState('')

  // Device enrollment
  const [devices, setDevices]               = useState<Device[]>([])
  const [selectedDevices, setSelectedDevices] = useState<string[]>([])

  useEffect(() => {
    getDevices().then(r => setDevices(r.devices)).catch(() => {})
  }, [])

  function toggleDevice(id: string) {
    setSelectedDevices(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  const initials = name.trim().split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?'

  function validate() {
    const e: Record<string, string> = {}
    if (!name.trim())       e.name       = 'El nombre es obligatorio'
    // Must be digits only — this becomes the device's employeeNo, and the
    // device rejects (badJsonContent:employeeNo) anything with letters or
    // symbols. Letting one through here means it fails silently later, deep
    // in the sync queue, with no obvious link back to "the cédula was bad".
    if (!employeeNo.trim())            e.employeeNo = 'La cédula es obligatoria'
    else if (!/^\d+$/.test(employeeNo.trim())) e.employeeNo = 'La cédula solo puede tener números, sin letras ni símbolos'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    // Nobody should walk away from "crear miembro" having forgotten to give the
    // person access to any device — ask once, explicitly, before saving.
    if (selectedDevices.length === 0) {
      setShowDeviceReminder(true)
      return
    }
    void doSave()
  }

  async function doSave() {
    setLoading(true)
    try {
      const isUnlimited = memberCategory !== 'client' || longTerm
      const payload = {
        name, employeeNo, userType, longTerm: isUnlimited, memberCategory,
        beginTime: beginTime + ':00',
        endTime: isUnlimited ? '2036-06-04T23:59:59' : endTime + ':00',
        phone:                phone                || undefined,
        email:                email                || undefined,
        documentType:         documentType         || undefined,
        idNumber:             employeeNo           || undefined,
        birthDate:            birthDate            || undefined,
        bloodType:            bloodType            || undefined,
        gender:               gender               || undefined,
        address:              address              || undefined,
        occupation:           occupation           || undefined,
        emergencyContactName: emergencyName        || undefined,
        emergencyContactPhone:emergencyPhone       || undefined,
        goal:                 goal                 || undefined,
        medicalNotes:         medicalNotes         || undefined,
        department:           department           || undefined,
        company:              company              || undefined,
        userVerifyMode:       userVerifyMode       || undefined,
      }
      const { user: created } = await createUser(payload)
      if (selectedDevices.length > 0) {
        await Promise.allSettled(
          selectedDevices.map(deviceId => enrollUsersToDevice(deviceId, [created.id]))
        )
        toast.success(`Miembro creado y agregado a ${selectedDevices.length} dispositivo${selectedDevices.length !== 1 ? 's' : ''}`)
      } else {
        toast.success('Miembro creado — ahora puedes agregar foto y métodos de acceso')
      }
      navigate(`/members/${created.id}`)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al guardar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">

      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/members')} className="text-text-muted hover:text-text-primary transition-colors" aria-label="Volver">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-text-muted text-sm">Miembros</span>
        <span className="text-text-muted text-sm">/</span>
        <span className="text-text-primary text-sm font-semibold">
          {name.trim() || 'Nuevo miembro'}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-[1fr_260px] gap-6 items-start">

        {/* ── Left: fields ── */}
        <div className="bg-surface border border-border rounded-xl p-6 flex flex-col gap-5">

          {/* Nombre + N° empleado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="text-text-muted text-xs font-medium uppercase tracking-wide mb-1 block">Nombre completo *</label>
              <input
                id="name" value={name}
                onChange={e => { setName(e.target.value); if (errors.name) setErrors(p => ({ ...p, name: '' })) }}
                className={`${field} ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="employeeNo" className="text-text-muted text-xs font-medium uppercase tracking-wide mb-1 block">
                Cédula *
              </label>
              <input
                id="employeeNo" value={employeeNo}
                onChange={e => { setEmployeeNo(e.target.value); if (errors.employeeNo) setErrors(p => ({ ...p, employeeNo: '' })) }}
                placeholder="Ej: 1234567890"
                className={`${field} ${errors.employeeNo ? 'border-red-500' : ''}`}
              />
              {errors.employeeNo && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.employeeNo}</p>}
            </div>
          </div>

          {/* Categoría de la persona */}
          <div>
            <p className="text-text-muted text-xs font-medium uppercase tracking-wide mb-2">Categoría</p>
            <div className="flex flex-wrap gap-2">
              {MEMBER_CATEGORIES.map(c => (
                <button key={c.value} type="button"
                  onClick={() => { setMemberCategory(c.value); setLongTerm(c.value !== 'client') }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${memberCategory === c.value ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            {memberCategory !== 'client' && (
              <p className="text-text-muted text-xs mt-1.5">
                {memberCategory === 'staff' ? 'Personal del establecimiento' : 'Socio'} — acceso ilimitado, sin depender de una membresía.
              </p>
            )}
          </div>

          {/* Validez del acceso */}
          <div>
            <p className="text-text-muted text-xs font-medium uppercase tracking-wide mb-2">Validez del acceso</p>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setLongTerm(true)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${longTerm ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary'}`}>
                Sin vencimiento
              </button>
              <button type="button" disabled={memberCategory !== 'client'} onClick={() => setLongTerm(false)}
                title={memberCategory !== 'client' ? 'Esta categoría siempre tiene acceso sin vencimiento' : undefined}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${!longTerm ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary'}`}>
                Fecha límite
              </button>
            </div>
            {!longTerm && memberCategory === 'client' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Inicio</label>
                  <input type="datetime-local" value={beginTime} onChange={e => setBeginTime(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={lbl}>Fin</label>
                  <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} className={field} />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border" />

          {/* ── Datos personales (collapsible) ── */}
          <button type="button" onClick={() => setShowGymData(p => !p)}
            className="flex items-center gap-1.5 text-sm font-medium text-text-primary hover:text-accent transition-colors self-start">
            {showGymData ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Datos personales
          </button>

          {showGymData && (
            <div className="flex flex-col gap-4">
              <div className="col-span-2">
                <label className={lbl}>Cédula / Documento de identidad</label>
                <div className="flex gap-2">
                  <select value={documentType} onChange={e => setDocumentType(e.target.value)}
                    className="shrink-0 w-24 bg-background border border-border rounded-md px-2 py-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent">
                    <option value="">Tipo</option>
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                    <option value="TI">TI</option>
                    <option value="PA">PA</option>
                    <option value="NIT">NIT</option>
                    <option value="RC">RC</option>
                    <option value="Otro">Otro</option>
                  </select>
                  <input value={employeeNo} readOnly
                    className="flex-1 min-w-0 bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary opacity-60 cursor-not-allowed"
                    title="Mismo número ingresado como cédula" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Teléfono / Celular</label><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="3001234567" className={field} /></div>
                <div><label className={lbl}>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@ejemplo.com" className={field} /></div>
                <div>
                  <label className={lbl}>
                    Fecha de nacimiento
                    {calcAge(birthDate) !== null && <span className="ml-1 text-accent font-semibold">{calcAge(birthDate)} años</span>}
                  </label>
                  <input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={lbl}>Tipo de sangre</label>
                  <select value={bloodType} onChange={e => setBloodType(e.target.value)} className={field}>
                    <option value="">Sin especificar</option>
                    {BLOOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Género</label>
                  <select value={gender} onChange={e => setGender(e.target.value)} className={field}>
                    <option value="">Sin especificar</option>
                    <option value="male">Masculino</option>
                    <option value="female">Femenino</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div><label className={lbl}>Ocupación</label><input value={occupation} onChange={e => setOccupation(e.target.value)} placeholder="Estudiante, contador..." className={field} /></div>
                <div><label className={lbl}>Dirección</label><input value={address} onChange={e => setAddress(e.target.value)} placeholder="Barrio, ciudad..." className={field} /></div>
              </div>

              <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mt-1">Contacto de emergencia</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Nombre</label><input value={emergencyName} onChange={e => setEmergencyName(e.target.value)} placeholder="Nombre y apellido" className={field} /></div>
                <div><label className={lbl}>Teléfono</label><input value={emergencyPhone} onChange={e => setEmergencyPhone(e.target.value)} placeholder="3009876543" className={field} /></div>
              </div>

              <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mt-1">Objetivo de entrenamiento</p>
              <div className="flex flex-wrap gap-2">
                {GYM_GOALS.map(g => (
                  <button key={g} type="button" onClick={() => setGoal(goal === g ? '' : g)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${goal === g ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary hover:border-accent/40'}`}>
                    {g}
                  </button>
                ))}
              </div>

              <div>
                <label className={lbl}>Observaciones médicas</label>
                <textarea rows={2} value={medicalNotes} onChange={e => setMedicalNotes(e.target.value)}
                  placeholder="Lesiones, medicamentos, condiciones..." className={`${field} resize-none`} />
              </div>
            </div>
          )}

          <div className="border-t border-border" />

          {/* ── Opciones avanzadas de dispositivo (collapsible) ── */}
          <button type="button" onClick={() => setShowAdvanced(p => !p)}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors self-start">
            {showAdvanced ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Opciones de dispositivo
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={lbl}>Tipo (dispositivo)</label>
                <div className="flex flex-wrap gap-2">
                  {USER_TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => setUserType(t.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${userType === t.value ? 'bg-accent text-background border-accent' : 'bg-transparent text-text-muted border-border hover:text-text-primary'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-text-muted text-xs mt-1">Categoría técnica que el lector reconoce — casi siempre "Miembro regular", sin importar si es Cliente, Personal o Socio.</p>
              </div>
              <div><label className={lbl}>Departamento</label><input value={department} onChange={e => setDepartment(e.target.value)} className={field} /></div>
              <div><label className={lbl}>Empresa</label><input value={company} onChange={e => setCompany(e.target.value)} className={field} /></div>
              <div className="col-span-2">
                <label className={lbl}>Modo de autenticación</label>
                <select value={userVerifyMode} onChange={e => setUserVerifyMode(e.target.value)} className={field}>
                  {VERIFY_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => navigate('/members')} disabled={loading}
              className="flex-1 border border-border text-text-primary text-sm py-2 rounded-md hover:bg-background transition-colors disabled:opacity-60">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-accent text-background text-sm font-semibold py-2 rounded-md hover:bg-accent-hover disabled:opacity-60 transition-colors">
              {loading ? 'Guardando...' : 'Crear miembro'}
            </button>
          </div>
        </div>

        {/* ── Right: live preview ── */}
        <div className="bg-surface border border-border rounded-xl p-5 flex flex-col items-center gap-3 text-center sticky top-6">
          <div className="w-20 h-20 rounded-full bg-accent/10 border-2 border-dashed border-accent/30 flex flex-col items-center justify-center gap-1">
            <span className="text-accent font-bold text-2xl leading-none">{initials}</span>
            <span className="text-[9px] text-text-muted">sin foto</span>
          </div>

          <div>
            <p className="text-text-primary font-bold text-sm">{name.trim() || 'Nombre del miembro'}</p>
            <p className="text-text-muted text-xs">#{employeeNo.trim() || '—'}</p>
          </div>

          <span className="text-xs px-3 py-1 rounded-full border border-border text-text-muted">
            {USER_TYPES.find(t => t.value === userType)?.label ?? 'Miembro regular'}
          </span>

          {(phone || (birthDate && calcAge(birthDate) !== null)) && (
            <div className="flex flex-col gap-0.5 text-xs text-text-muted w-full border-t border-border pt-2">
              {birthDate && calcAge(birthDate) !== null && <span>{calcAge(birthDate)} años</span>}
              {phone && <span>{phone}</span>}
            </div>
          )}

          <div className="w-full border-t border-border pt-3 mt-1 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-text-muted text-xs bg-background/60 rounded-lg px-3 py-2.5">
                <Camera className="w-4 h-4 shrink-0 text-accent/60" />
                <span className="text-left">La foto y los métodos de acceso se configuran en el perfil del miembro</span>
              </div>

              {/* ── Dispositivos ── */}
              <div className="w-full">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2 flex items-center gap-1.5">
                  <Monitor className="w-3 h-3" /> Agregar a dispositivo
                </p>
                {devices.length === 0 ? (
                  <p className="text-text-muted text-xs text-center py-2 opacity-60">Sin dispositivos registrados</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {devices.map(d => {
                      const checked = selectedDevices.includes(d.id)
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => toggleDevice(d.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors cursor-pointer ${
                            checked
                              ? 'bg-accent/8 border-accent/30 text-accent'
                              : 'border-border hover:bg-background/60 text-text-muted'
                          }`}
                        >
                          {checked
                            ? <CheckSquare className="w-3.5 h-3.5 shrink-0" />
                            : <Square className="w-3.5 h-3.5 shrink-0" />
                          }
                          <span className="text-xs font-medium truncate">{d.name}</span>
                          <span className={`ml-auto w-1.5 h-1.5 rounded-full shrink-0 ${
                            d.status === 'online' ? 'bg-emerald-500' : 'bg-border'
                          }`} />
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
          </div>
        </div>

      </form>

      {/* ── Recordatorio: no se seleccionó ningún dispositivo ── */}
      <Dialog open={showDeviceReminder} onOpenChange={setShowDeviceReminder}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader><DialogTitle>¿En qué dispositivo le das acceso?</DialogTitle></DialogHeader>
          <p className="text-text-muted text-sm mt-2">
            No seleccionaste ningún dispositivo para <strong className="text-text-primary">{name || 'este miembro'}</strong>. Puedes elegir uno abajo en el formulario, o continuar sin acceso y agregarlo después.
          </p>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowDeviceReminder(false)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors cursor-pointer">
              Elegir dispositivo
            </button>
            <button
              onClick={() => { setShowDeviceReminder(false); void doSave() }}
              className="px-4 py-2 bg-accent text-white text-sm rounded-lg hover:bg-accent/90 transition-colors cursor-pointer"
            >
              Continuar sin dispositivo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
